import React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { ErrorView } from '../../../views/error/ErrorView'
import { IllustrationChoices } from '../../common/Illustration'
import { ConversationsList } from '../Conversations'
import { ToastType } from '../../common/Toast/toast-type'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { useSelectedChatId } from '../../../state/context/SelectedChatIdProvider'
import { setRoomArchived } from '../../../api/chat'
import {
  ConversationRow,
  ConversationType,
} from '../../../generated/api/chat-conversations'
import { invalidateRoomSettingsQueries } from '../Room/useRoomMessagesCache'

interface FindArchiveChatsModalContentProps {
  modalRef: React.MutableRefObject<HTMLDivElement | null>
  close: () => void
  type: ConversationType
}

export const FindArchiveChatsModalContent = ({
  modalRef,
  close,
  type,
}: FindArchiveChatsModalContentProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const queryClient = useQueryClient()

  const { setSelectedChatRoomId } = useSelectedChatId()
  const { mutate: unarchive } = useMutation({
    mutationFn: async (roomId: string) =>
      (await setRoomArchived(roomId, false)).data.data,
    meta: { skipGlobalInvalidate: true },
    onSuccess: ({ roomId }) => {
      invalidateRoomSettingsQueries(queryClient, roomId)
      setToastNotification(
        ToastType.SUCCESS,
        translate('findArchivedChats.flashMessage.activateArchivedChatSuccess')
      )
      setSelectedChatRoomId()
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('findArchivedChats.flashMessage.activateArchivedChatFailure')
      )
    },
  })

  const onItemSelect = (row: ConversationRow): void => {
    unarchive(row.id)
    close()
  }

  return (
    <div className="flex flex-col">
      <ConversationsList
        type={type}
        state="archived"
        rootRef={modalRef}
        rowClassName="border-b border-neutral-80 p-4 hover:bg-neutral-96"
        onRowClick={onItemSelect}
        emptyState={
          <ErrorView
            title={translate('findArchivedChats.errorViews.noChatRooms.title')}
            subtitle={translate(
              'findArchivedChats.errorViews.noChatRooms.subtitle'
            )}
            illustration={IllustrationChoices.EMPTY}
            illustrationHeight={250}
            illustrationWidth={187}
          />
        }
      />
    </div>
  )
}
